import { AlgorithmResult, StatisticalMetrics } from '../types';

/**
 * Calculates statistical metrics (mean, std dev, min, max, median) for a set of values
 */
export function calculateStatistics(values: number[]): StatisticalMetrics {
  if (values.length === 0) {
    return {
      mean: 0,
      stdDev: 0,
      min: 0,
      max: 0,
      median: 0,
    };
  }

  // Sort values for median calculation
  const sorted = [...values].sort((a, b) => a - b);
  
  // Calculate mean
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  
  // Calculate standard deviation
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  
  // Min, max, median
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const median = sorted.length % 2 === 0
    ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
    : sorted[Math.floor(sorted.length / 2)];

  return {
    mean,
    stdDev,
    min,
    max,
    median,
  };
}

/**
 * Calculates statistics for an algorithm across multiple runs
 */
export function calculateAlgorithmStatistics(
  results: AlgorithmResult[]
): {
  fitness: StatisticalMetrics;
  energy: StatisticalMetrics;
  cost: StatisticalMetrics;
  responseTime: StatisticalMetrics;
  executionTime: StatisticalMetrics;
} {
  if (results.length === 0) {
    const emptyStats: StatisticalMetrics = {
      mean: 0,
      stdDev: 0,
      min: 0,
      max: 0,
      median: 0,
    };
    return {
      fitness: emptyStats,
      energy: emptyStats,
      cost: emptyStats,
      responseTime: emptyStats,
      executionTime: emptyStats,
    };
  }

  const fitnessValues = results.map(r => r.fitness);
  const energyValues = results.map(r => r.energy);
  const costValues = results.map(r => r.cost);
  const responseTimeValues = results.map(r => r.responseTime);
  const executionTimeValues = results.map(r => r.executionTime || 0);

  return {
    fitness: calculateStatistics(fitnessValues),
    energy: calculateStatistics(energyValues),
    cost: calculateStatistics(costValues),
    responseTime: calculateStatistics(responseTimeValues),
    executionTime: calculateStatistics(executionTimeValues),
  };
}

